'use client'

import React, { useState, useCallback, useRef, useEffect, useImperativeHandle, forwardRef } from 'react'
import { IChartApi, UTCTimestamp, LineSeries, LineStyle } from 'lightweight-charts'
import { 
  Drawing,
  DrawingType,
  DrawingState,
  DrawingEvent,
  Point,
  DEFAULT_DRAWING_STYLE,
  FibonacciRetracement,
  FibonacciExtension,
  Channel,
  PatternDrawing,
  HeadAndShouldersPattern,
  DoubleTopBottomPattern,
  TrianglePattern,
  PatternPoint
} from '../types/drawing'

// Import drawing components
import FibonacciRetracementComponent, { createFibonacciRetracement } from './drawings/FibonacciRetracement'
import FibonacciExtensionComponent, { createFibonacciExtension } from './drawings/FibonacciExtension'
import ChannelComponent, { createParallelChannel, createStandardChannel } from './drawings/Channel'

// Import pattern recognition components
import PatternManager, { createPatternFromPoints, detectPatterns } from './PatternManager'
import PatternNotification from './PatternNotification'

// Create basic trendline function
const createTrendLine = (id: string, startPoint: Point, endPoint: Point): Drawing => {
  return {
    id,
    type: 'trendline',
    points: [startPoint, endPoint],
    style: DEFAULT_DRAWING_STYLE,
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 1
  }
}

// Simple TrendLine component using useEffect to add/remove line series
const TrendLineComponent: React.FC<{
  drawing: Drawing
  chart: IChartApi
  isActive: boolean
  onUpdate: (drawing: Drawing) => void
}> = ({ drawing, chart, isActive }) => {
  useEffect(() => {
    if (drawing.points.length < 2) return

    const lineSeries = chart.addSeries(LineSeries, {
      color: drawing.style.lineColor,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
    })

    const lineData = drawing.points
      .map(point => ({
        time: point.time as UTCTimestamp,
        value: point.price
      }))
      .sort((a, b) => a.time - b.time)

    lineSeries.setData(lineData)

    return () => {
      try {
        if (chart && lineSeries) {
          chart.removeSeries(lineSeries)
        }
      } catch (error) {
        // Safely ignore cleanup errors when chart is destroyed
        console.warn('Failed to remove series during cleanup:', error)
      }
    }
  }, [drawing, chart])

  return null
}

// Import toolbar
import DrawingToolbar, { DrawingToolType } from './DrawingToolbar'

interface DrawingManagerProps {
  chart: IChartApi | null
  mainSeries: any | null
  isVisible: boolean // This should only control toolbar visibility, not drawing visibility
  onDrawingComplete?: (drawing: Drawing) => void
  onDrawingUpdate?: (drawing: Drawing) => void
  onDrawingDelete?: (drawingId: string) => void
  onClearAllDrawings?: () => void // Expose clear function to parent
  onAIAnalysis?: () => void // AI analysis callback
  chartData?: Array<{ time: number, open: number, high: number, low: number, close: number }> // For pattern detection
}

export interface DrawingManagerRef {
  clearAllDrawings: () => void
}

/**
 * Drawing Manager Component
 * 
 * Manages all drawing tools and their interactions:
 * - Provides drawing toolbar with tool selection
 * - Handles mouse events for drawing creation
 * - Manages drawing state and history
 * - Renders active drawings on the chart
 * - Supports undo/redo functionality
 */
const DrawingManager = forwardRef<DrawingManagerRef, DrawingManagerProps>(({
  chart,
  mainSeries,
  isVisible,
  onDrawingComplete,
  onDrawingUpdate,
  onDrawingDelete,
  onClearAllDrawings,
  onAIAnalysis,
  chartData
}, ref) => {
  // Drawing state management
  const [drawingState, setDrawingState] = useState<DrawingState>({
    drawings: new Map(),
    activeDrawing: null,
    selectedDrawings: new Set(),
    activeTool: 'select',
    isDrawing: false,
    history: [],
    historyIndex: -1,
    config: {
      snapToPrice: true,
      snapTolerance: 0.01,
      showTooltips: true,
      enableMagnetMode: false,
      defaultStyle: DEFAULT_DRAWING_STYLE
    }
  })

  // Drawing creation state
  const [creationPoints, setCreationPoints] = useState<Point[]>([])
  const [isCreating, setIsCreating] = useState(false)
  
  // Mouse event tracking
  const mouseDownRef = useRef<Point | null>(null)
  const isMouseDownRef = useRef(false)

  // Notification state
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'warning' | 'error' | 'info'
  } | null>(null)
  
  const [patternNotification, setPatternNotification] = useState<{
    patterns: PatternDrawing[]
    currentPrice: number
  } | null>(null)

  // Generate unique ID for drawings
  const generateDrawingId = useCallback(() => {
    return `drawing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }, [])

  // Handle tool selection
  const handleToolSelect = useCallback((tool: DrawingToolType) => {
    setDrawingState(prev => ({
      ...prev,
      activeTool: tool,
      isDrawing: false,
      activeDrawing: null
    }))
    setCreationPoints([])
    // Set isCreating to true if we're selecting a drawing tool (not select tool)
    setIsCreating(tool !== 'select')
  }, [])

  // Handle mouse events for drawing creation
  const handleChartClick = useCallback((point: Point) => {
    if (!chart || drawingState.activeTool === 'select') return

    const newPoint: Point = {
      time: point.time,
      price: point.price
    }

    setCreationPoints(prev => {
      const updatedPoints = [...prev, newPoint]
      
      // Determine if we have enough points to complete the drawing
      const requiredPoints = getRequiredPointsForTool(drawingState.activeTool)
      
      if (updatedPoints.length >= requiredPoints) {
        // Create the drawing
        createDrawingFromPoints(drawingState.activeTool, updatedPoints)
        // Reset creation state after completing the drawing
        setIsCreating(false)
        return []
      }
      
      return updatedPoints
    })
  }, [chart, drawingState.activeTool])

  // Get required points for each drawing tool
  const getRequiredPointsForTool = (tool: DrawingToolType): number => {
    switch (tool) {
      case 'trendline':
      case 'horizontal':
      case 'vertical':
        return 2
      case 'fibonacci-retracement':
        return 2
      case 'fibonacci-extension':
        return 3 // A, B, C points
      case 'channel':
        return 3 // Start, end, width point
      case 'parallel-channel':
        return 2 // Start, end (width calculated)
      case 'rectangle':
        return 2
      case 'text':
        return 1
      // Pattern Recognition Tools
      case 'head-and-shoulders':
        return 5 // Left shoulder, left valley, head, right valley, right shoulder
      case 'double-top':
      case 'double-bottom':
        return 3 // First peak, valley, second peak
      case 'ascending-triangle':
      case 'descending-triangle':
      case 'symmetrical-triangle':
        return 4 // Minimum 2 highs and 2 lows
      case 'rising-wedge':
      case 'falling-wedge':
        return 4 // Similar to triangles
      case 'bull-flag':
      case 'bear-flag':
      case 'pennant':
        return 4 // Pole start, pole end, consolidation high, consolidation low
      default:
        return 2
    }
  }

  // Create drawing from collected points
  const createDrawingFromPoints = useCallback((tool: DrawingToolType, points: Point[]) => {
    const id = generateDrawingId()
    
    let newDrawing: Drawing | null = null

    switch (tool) {
      case 'trendline':
        if (points.length >= 2) {
          newDrawing = createTrendLine(id, points[0], points[1])
        }
        break
        
      case 'fibonacci-retracement':
        if (points.length >= 2) {
          newDrawing = createFibonacciRetracement(id, points[0], points[1])
        }
        break
        
      case 'fibonacci-extension':
        if (points.length >= 3) {
          newDrawing = createFibonacciExtension(id, points[0], points[1], points[2])
        }
        break
        
      case 'channel':
        if (points.length >= 3) {
          newDrawing = createStandardChannel(id, points[0], points[1], points[2])
        }
        break
        
      case 'parallel-channel':
        if (points.length >= 2) {
          // Calculate width based on price difference (simplified)
          const width = Math.abs(points[1].price - points[0].price) * 0.5
          newDrawing = createParallelChannel(id, points[0], points[1], width)
        }
        break
        
      // Pattern Recognition Tools
      case 'head-and-shoulders':
        if (points.length >= 5) {
          const patternPoints: PatternPoint[] = points.map((point, index) => ({
            ...point,
            role: index % 2 === 0 ? 'peak' : 'valley',
            significance: index === 2 ? 1.0 : 0.8 // Head has highest significance
          }))
          newDrawing = createPatternFromPoints('head-and-shoulders', patternPoints, id)
        }
        break
        
      case 'double-top':
      case 'double-bottom':
        if (points.length >= 3) {
          const patternPoints: PatternPoint[] = points.map((point, index) => ({
            ...point,
            role: tool === 'double-top' ? 
              (index === 1 ? 'support' : 'resistance') :
              (index === 1 ? 'resistance' : 'support'),
            significance: index === 1 ? 0.7 : 0.9 // Peaks/valleys more significant
          }))
          newDrawing = createPatternFromPoints(tool, patternPoints, id)
        }
        break
        
      case 'ascending-triangle':
      case 'descending-triangle':
      case 'symmetrical-triangle':
        if (points.length >= 4) {
          const patternPoints: PatternPoint[] = points.map((point, index) => ({
            ...point,
            role: index % 2 === 0 ? 'resistance' : 'support',
            significance: 0.8
          }))
          newDrawing = createPatternFromPoints(tool, patternPoints, id)
        }
        break
        
      case 'rising-wedge':
      case 'falling-wedge':
        if (points.length >= 4) {
          const patternPoints: PatternPoint[] = points.map((point, index) => ({
            ...point,
            role: tool === 'rising-wedge' ? 
              (index % 2 === 0 ? 'resistance' : 'support') :
              (index % 2 === 0 ? 'support' : 'resistance'),
            significance: 0.8
          }))
          newDrawing = createPatternFromPoints(tool, patternPoints, id)
        }
        break
        
      case 'bull-flag':
      case 'bear-flag':
      case 'pennant':
        if (points.length >= 4) {
          const patternPoints: PatternPoint[] = points.map((point, index) => ({
            ...point,
            role: index < 2 ? 'breakout' : (index === 2 ? 'resistance' : 'support'),
            significance: index < 2 ? 1.0 : 0.7 // Pole more significant
          }))
          newDrawing = createPatternFromPoints(tool, patternPoints, id)
        }
        break
        
      // TODO: Add other drawing types (horizontal, vertical, rectangle, text)
      default:
        console.warn(`Drawing tool ${tool} not implemented yet`)
        break
    }

    if (newDrawing) {
      // Add to state
      setDrawingState(prev => {
        const newDrawings = new Map(prev.drawings)
        newDrawings.set(id, newDrawing!)
        
        // Add to history for undo/redo
        const newHistory = prev.history.slice(0, prev.historyIndex + 1)
        newHistory.push(Array.from(newDrawings.values()))
        
        return {
          ...prev,
          drawings: newDrawings,
          activeDrawing: newDrawing!,
          history: newHistory,
          historyIndex: newHistory.length - 1
        }
      })

      // Notify parent
      onDrawingComplete?.(newDrawing)
      
      // Reset creation state
      setIsCreating(false)
      handleToolSelect('select')
    }
  }, [generateDrawingId, onDrawingComplete, handleToolSelect])

  // Handle drawing updates
  const handleDrawingUpdate = useCallback((drawing: Drawing) => {
    setDrawingState(prev => {
      const newDrawings = new Map(prev.drawings)
      newDrawings.set(drawing.id, { ...drawing, updatedAt: Date.now() })
      
      return {
        ...prev,
        drawings: newDrawings
      }
    })
    
    onDrawingUpdate?.(drawing)
  }, [onDrawingUpdate])

  // Handle drawing deletion
  const handleDrawingDelete = useCallback((drawingId: string) => {
    setDrawingState(prev => {
      const newDrawings = new Map(prev.drawings)
      newDrawings.delete(drawingId)
      
      const newSelectedDrawings = new Set(prev.selectedDrawings)
      newSelectedDrawings.delete(drawingId)
      
      return {
        ...prev,
        drawings: newDrawings,
        selectedDrawings: newSelectedDrawings,
        activeDrawing: prev.activeDrawing?.id === drawingId ? null : prev.activeDrawing
      }
    })
    
    onDrawingDelete?.(drawingId)
  }, [onDrawingDelete])

  // Undo/Redo functionality
  const handleUndo = useCallback(() => {
    setDrawingState(prev => {
      if (prev.historyIndex <= 0) return prev
      
      const newIndex = prev.historyIndex - 1
      const previousState = prev.history[newIndex] || []
      const newDrawings = new Map(previousState.map(d => [d.id, d]))
      
      return {
        ...prev,
        drawings: newDrawings,
        historyIndex: newIndex,
        activeDrawing: null,
        selectedDrawings: new Set()
      }
    })
  }, [])

  const handleRedo = useCallback(() => {
    setDrawingState(prev => {
      if (prev.historyIndex >= prev.history.length - 1) return prev
      
      const newIndex = prev.historyIndex + 1
      const nextState = prev.history[newIndex] || []
      const newDrawings = new Map(nextState.map(d => [d.id, d]))
      
      return {
        ...prev,
        drawings: newDrawings,
        historyIndex: newIndex,
        activeDrawing: null,
        selectedDrawings: new Set()
      }
    })
  }, [])

  // Clear all drawings
  const handleClearAll = useCallback(() => {
    setDrawingState(prev => {
      const newHistory = [...prev.history, []]
      
      return {
        ...prev,
        drawings: new Map(),
        activeDrawing: null,
        selectedDrawings: new Set(),
        history: newHistory,
        historyIndex: newHistory.length - 1
      }
    })
    
    // Notify parent component that drawings have been cleared
    if (onClearAllDrawings) {
      onClearAllDrawings()
    }
  }, [onClearAllDrawings])

  // Expose clear function to parent component via ref
  useImperativeHandle(ref, () => ({
    clearAllDrawings: handleClearAll
  }), [handleClearAll])

  // Auto-detect patterns function
  const handleAutoDetectPatterns = useCallback(() => {
    console.log('🔍 Pattern Detection Debug:', {
      chartDataExists: !!chartData,
      chartDataLength: chartData?.length || 0,
      chartDataSample: chartData?.slice(0, 3) || 'No data'
    })
    
    if (!chartData || chartData.length < 20) {
      setNotification({
        message: `Not enough chart data for pattern detection. Found ${chartData?.length || 0} data points, need at least 20.`,
        type: 'warning'
      })
      return
    }

    try {
      setNotification({
        message: 'Scanning chart for patterns...',
        type: 'info'
      })

      // Detect patterns with minimum confidence of 70%
      const detectedPatterns = detectPatterns(chartData, 0.7)
      
      if (detectedPatterns.length === 0) {
        setNotification({
          message: 'No patterns detected in the current chart data. Try adjusting the timeframe or looking at different price movements.',
          type: 'warning'
        })
        return
      }

      // Add detected patterns to the drawing state
      setDrawingState(prev => {
        const newDrawings = new Map(prev.drawings)
        
        detectedPatterns.forEach(pattern => {
          newDrawings.set(pattern.id, pattern)
          // Notify parent of new drawing
          onDrawingComplete?.(pattern)
        })
        
        // Add to history for undo/redo
        const newHistory = prev.history.slice(0, prev.historyIndex + 1)
        newHistory.push(Array.from(newDrawings.values()))
        
        return {
          ...prev,
          drawings: newDrawings,
          history: newHistory,
          historyIndex: newHistory.length - 1
        }
      })

      // Show trading recommendations for detected patterns
      const currentPrice = chartData[chartData.length - 1]?.close || 0
      
      setPatternNotification({
        patterns: detectedPatterns,
        currentPrice: currentPrice
      })
      
    } catch (error) {
      console.error('Error detecting patterns:', error)
      setNotification({
        message: 'Error occurred while detecting patterns. Please try again.',
        type: 'error'
      })
    }
  }, [chartData, onDrawingComplete])

  // Set up chart click listeners when chart is available
  useEffect(() => {
    if (!chart || !mainSeries) return

    const clickHandler = (param: any) => {
      // Only handle clicks if toolbar is visible (isVisible) and we're in drawing mode
      if (!isVisible || drawingState.activeTool === 'select') return
      if (!param.point || !param.time || !mainSeries) return
      
      const point: Point = {
        time: param.time as number,
        price: mainSeries.coordinateToPrice(param.point.y) as number
      }
      
      handleChartClick(point)
    }

    chart.subscribeClick(clickHandler)
    
    return () => {
      chart.unsubscribeClick(clickHandler)
    }
  }, [chart, mainSeries, isVisible, handleChartClick, drawingState.activeTool])

  // Render all drawings
  const renderDrawings = () => {
    if (!chart) return null

    return Array.from(drawingState.drawings.values()).map(drawing => {
      const isActive = drawing.id === drawingState.activeDrawing?.id
      
      switch (drawing.type) {
        case 'trendline':
          return (
            <TrendLineComponent
              key={drawing.id}
              drawing={drawing}
              chart={chart}
              isActive={isActive}
              onUpdate={handleDrawingUpdate}
            />
          )
          
        case 'fibonacci-retracement':
          return (
            <FibonacciRetracementComponent
              key={drawing.id}
              drawing={drawing as FibonacciRetracement}
              chart={chart}
              isActive={isActive}
              onUpdate={handleDrawingUpdate}
            />
          )
          
        case 'fibonacci-extension':
          return (
            <FibonacciExtensionComponent
              key={drawing.id}
              drawing={drawing as FibonacciExtension}
              chart={chart}
              isActive={isActive}
              onUpdate={handleDrawingUpdate}
            />
          )
          
        case 'channel':
        case 'parallel-channel':
          return (
            <ChannelComponent
              key={drawing.id}
              drawing={drawing as Channel}
              chart={chart}
              isActive={isActive}
              onUpdate={handleDrawingUpdate}
            />
          )
          
        // Pattern Recognition Tools
        case 'head-and-shoulders':
        case 'double-top':
        case 'double-bottom':
        case 'ascending-triangle':
        case 'descending-triangle':
        case 'symmetrical-triangle':
        case 'rising-wedge':
        case 'falling-wedge':
        case 'bull-flag':
        case 'bear-flag':
        case 'pennant':
          return null // Patterns are handled by PatternManager below
          
        default:
          return null
      }
    })
  }

  // Always render drawings, but only show toolbar when isVisible is true
  return (
    <div style={{ position: 'relative', zIndex: 1000 }}>
      {/* Drawing Toolbar - only show when isVisible is true */}
      {isVisible && (
        <DrawingToolbar
          activeTool={drawingState.activeTool}
          onToolSelect={handleToolSelect}
          onClearAll={handleClearAll}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={drawingState.historyIndex > 0}
          canRedo={drawingState.historyIndex < drawingState.history.length - 1}
          onAutoDetectPatterns={handleAutoDetectPatterns}
          onAIAnalysis={onAIAnalysis || (() => {})}
        />
      )}
      
      {/* Drawing Components - always render existing drawings */}
      {renderDrawings()}
      
      {/* Pattern Recognition Components */}
      {chart && (
        <PatternManager
          chart={chart}
          patterns={new Map(
            Array.from(drawingState.drawings.entries())
              .filter(([_, drawing]) => 
                ['head-and-shoulders', 'double-top', 'double-bottom', 
                 'ascending-triangle', 'descending-triangle', 'symmetrical-triangle',
                 'rising-wedge', 'falling-wedge', 'bull-flag', 'bear-flag', 'pennant']
                .includes(drawing.type)
              )
              .map(([id, drawing]) => [id, drawing as PatternDrawing])
          )}
          onPatternUpdate={handleDrawingUpdate}
          onPatternDelete={handleDrawingDelete}
          isInteractive={isVisible}
          currentPrice={chartData?.[chartData.length - 1]?.close || 0}
        />
      )}
      
      {/* Creation Status - show when toolbar is visible and creating */}
      {isVisible && isCreating && drawingState.activeTool !== 'select' && (
        <div
          style={{
            position: 'absolute',
            top: '60px',
            left: '16px',
            background: 'rgba(42, 42, 42, 0.95)',
            color: '#E0E3EB',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: 1001,
            border: '1px solid #444',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            maxWidth: '300px',
            lineHeight: '1.4'
          }}
        >
          <div style={{ marginBottom: '4px', fontSize: '12px', opacity: 0.8 }}>
            {getToolDisplayName(drawingState.activeTool)}
          </div>
          {getCreationStatusText(drawingState.activeTool, creationPoints.length)}
        </div>
      )}
      
      {/* Simple Notification */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: notification.type === 'success' ? '#10b981' : 
                       notification.type === 'warning' ? '#fbbf24' : 
                       notification.type === 'error' ? '#ef4444' : '#3b82f6',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: 10001,
            maxWidth: '400px',
            wordBreak: 'break-word'
          }}
        >
          {notification.message}
          <button
            onClick={() => setNotification(null)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '12px',
              marginLeft: '8px'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Pattern Detection Notification with Trading Recommendations */}
      {patternNotification && (
        <PatternNotification
          patterns={patternNotification.patterns}
          currentPrice={patternNotification.currentPrice}
          onClose={() => setPatternNotification(null)}
        />
      )}
    </div>
  )
})

// Add display name for better debugging
DrawingManager.displayName = 'DrawingManager'

// Helper function to get tool display name
const getToolDisplayName = (tool: DrawingToolType): string => {
  const toolNames = {
    'fibonacci-retracement': 'Fibonacci Retracement',
    'fibonacci-extension': 'Fibonacci Extension',
    'channel': 'Channel',
    'parallel-channel': 'Parallel Channel',
    'head-and-shoulders': 'Head & Shoulders Pattern',
    'double-top': 'Double Top Pattern',
    'double-bottom': 'Double Bottom Pattern',
    'ascending-triangle': 'Ascending Triangle Pattern',
    'descending-triangle': 'Descending Triangle Pattern',
    'symmetrical-triangle': 'Symmetrical Triangle Pattern',
    'rising-wedge': 'Rising Wedge Pattern',
    'falling-wedge': 'Falling Wedge Pattern',
    'bull-flag': 'Bull Flag Pattern',
    'bear-flag': 'Bear Flag Pattern',
    'pennant': 'Pennant Pattern',
    'trendline': 'Trend Line',
    'horizontal': 'Horizontal Line',
    'vertical': 'Vertical Line',
    'rectangle': 'Rectangle',
    'text': 'Text Note'
  }
  
  return toolNames[tool as keyof typeof toolNames] || 'Drawing Tool'
}

// Helper function to get creation status text
const getCreationStatusText = (tool: DrawingToolType, pointCount: number): string => {
  const requiredPoints = {
    'fibonacci-retracement': 2,
    'fibonacci-extension': 3,
    'channel': 3,
    'parallel-channel': 2,
    'head-and-shoulders': 5,
    'double-top': 3,
    'double-bottom': 3,
    'ascending-triangle': 4,
    'descending-triangle': 4,
    'symmetrical-triangle': 4,
    'rising-wedge': 4,
    'falling-wedge': 4,
    'bull-flag': 4,
    'bear-flag': 4,
    'pennant': 4
  }
  
  const required = requiredPoints[tool as keyof typeof requiredPoints] || 2
  const remaining = required - pointCount
  
  if (remaining <= 0) return 'Pattern complete!'
  
  const toolNames = {
    'fibonacci-retracement': 'Fibonacci Retracement',
    'fibonacci-extension': 'Fibonacci Extension',
    'channel': 'Channel',
    'parallel-channel': 'Parallel Channel',
    'head-and-shoulders': 'Head & Shoulders',
    'double-top': 'Double Top',
    'double-bottom': 'Double Bottom',
    'ascending-triangle': 'Ascending Triangle',
    'descending-triangle': 'Descending Triangle',
    'symmetrical-triangle': 'Symmetrical Triangle',
    'rising-wedge': 'Rising Wedge',
    'falling-wedge': 'Falling Wedge',
    'bull-flag': 'Bull Flag',
    'bear-flag': 'Bear Flag',
    'pennant': 'Pennant'
  }
  
  const toolName = toolNames[tool as keyof typeof toolNames] || 'Drawing'
  
  // Provide specific guidance for pattern creation
  if (tool === 'head-and-shoulders') {
    const stepNames = ['left shoulder', 'left valley', 'head', 'right valley', 'right shoulder']
    if (pointCount === 0) return `Click on the left shoulder peak to start`
    return `Click ${stepNames[pointCount]} (${remaining} more points)`
  } else if (tool === 'double-top' || tool === 'double-bottom') {
    const stepNames = ['first peak', 'valley', 'second peak']
    if (pointCount === 0) return `Click on the first ${tool === 'double-top' ? 'peak' : 'bottom'} to start`
    return `Click ${stepNames[pointCount]} (${remaining} more points)`
  } else if (tool.includes('triangle') || tool.includes('wedge')) {
    if (pointCount === 0) return `Click on a high point to start the pattern`
    return `Click alternating highs and lows (${remaining} more points)`
  } else if (tool.includes('flag') || tool === 'pennant') {
    const stepNames = ['pole start', 'pole end', 'consolidation high', 'consolidation low']
    if (pointCount === 0) return `Click at the start of the flagpole`
    return `Click ${stepNames[pointCount]} (${remaining} more points)`
  } else if (tool === 'fibonacci-retracement') {
    if (pointCount === 0) return `Click at the start of the price move`
    return `Click at the end of the price move`
  } else if (tool === 'fibonacci-extension') {
    const stepNames = ['point A', 'point B', 'point C']
    if (pointCount === 0) return `Click at point A (start of AB move)`
    return `Click ${stepNames[pointCount]} (${remaining} more points)`
  }
  
  if (pointCount === 0) return `Click on the chart to start drawing`
  return `Click ${remaining} more point${remaining > 1 ? 's' : ''}`
}

export default DrawingManager 